import React, { useEffect, useState } from 'react';
import { getAdminContactMessages, markContactMessageRead } from '../../services/api';
import { Loader2, Mail, MailOpen, Phone, Inbox, X } from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminMessages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await getAdminContactMessages();
      setItems(res.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openMessage = async (m) => {
    setActive(m);
    if (!m.read) {
      try {
        await markContactMessageRead(m.id);
        setItems((list) => list.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
      } catch (e) { /* ignore */ }
    }
  };

  const filtered = items.filter((m) => filter === 'all' || (filter === 'unread' ? !m.read : m.read));
  const unreadCount = items.filter((m) => !m.read).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-[#1B1B1B]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            İletişim Mesajları
          </h2>
          {unreadCount > 0 && (
            <span className="bg-[#C8D843] text-[#1B1B1B] text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount} yeni
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full p-1">
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'unread', label: 'Okunmamış' },
            { key: 'read', label: 'Okunmuş' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`messages-filter-${t.key}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                filter === t.key
                  ? 'bg-[#3D4A26] text-white'
                  : 'text-gray-500 hover:text-[#1B1B1B]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-[#3D4A26]" size={28} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
          <Inbox size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Bu filtreye uygun mesaj yok.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="w-10 px-4 py-3"></th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Gönderen</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Konu</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Önizleme</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Tarih</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => openMessage(m)}
                  data-testid={`message-row-${m.id}`}
                  className={`cursor-pointer hover:bg-gray-50 ${!m.read ? 'bg-[#fafdf0]' : ''}`}
                >
                  <td className="px-4 py-3">
                    {m.read ? (
                      <MailOpen size={16} className="text-gray-400" />
                    ) : (
                      <Mail size={16} className="text-[#3D4A26]" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className={`text-sm ${!m.read ? 'font-bold text-[#1B1B1B]' : 'text-gray-700'}`}>{m.name}</p>
                    <p className="text-xs text-gray-500">{m.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className={`text-sm ${!m.read ? 'font-semibold text-[#1B1B1B]' : 'text-gray-700'}`}>
                      {m.subject || <span className="text-gray-400 italic">(Konu yok)</span>}
                    </p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-xs text-gray-500 line-clamp-1 max-w-md">{m.message}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(m.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setActive(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-[#1B1B1B]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {active.subject || 'Mesaj'}
              </h3>
              <button
                onClick={() => setActive(null)}
                data-testid="message-close"
                className="p-1.5 hover:bg-gray-100 rounded-full"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-5">
              <div className="flex flex-wrap gap-4 mb-5 pb-5 border-b border-gray-100">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Gönderen</p>
                  <p className="font-semibold text-[#1B1B1B]">{active.name}</p>
                  <a href={`mailto:${active.email}`} className="text-sm text-[#3D4A26] hover:underline flex items-center gap-1 mt-0.5">
                    <Mail size={12} /> {active.email}
                  </a>
                  {active.phone && (
                    <a href={`tel:${active.phone}`} className="text-sm text-[#3D4A26] hover:underline flex items-center gap-1 mt-0.5">
                      <Phone size={12} /> {active.phone}
                    </a>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Tarih</p>
                  <p className="text-sm text-[#1B1B1B]">{formatDate(active.created_at)}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Mesaj</p>
                <p className="text-sm text-[#2a2a2a] whitespace-pre-wrap leading-relaxed">{active.message}</p>
              </div>

              <a
                href={`mailto:${active.email}?subject=Re: ${encodeURIComponent(active.subject || '')}`}
                className="inline-flex items-center gap-2 bg-[#3D4A26] text-white font-bold px-5 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors text-sm"
              >
                <Mail size={14} /> E-posta ile Cevapla
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
